import { useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, Building2, Check, ChevronDown, LockKeyhole, Mail, User, Users } from "lucide-react";
import { checkRegistrationDuplicate, clearRegistrationProgress, createRazorpayOrder, getRegistrationPlans, getRegistrationProgress, saveRegistrationProgress, sendEmailOtp, submitRegistration, verifyEmailOtp, verifyRazorpayPayment } from "../../api/registrationApi";
import { copy, initialRegistrationForm } from "./registrationData";
import { HomeHeader } from "../landing/LandingPage";
import { AUTH_PAGE_COPY } from "../../i18n/translations";
import "../landing/LandingPage.css";
import DashboardLandingStep from "./steps/DashboardLandingStep_8";

const BUSINESS_TYPES = ["Retail", "Trading", "Manufacturing", "Services", "Construction", "Logistics", "Clinic / Healthcare", "Salon / Beauty", "Other"];
const DEPARTMENTS = ["Sales", "Operations", "Accounts", "HR", "Production", "Logistics", "Other"];
const REGISTRATION_DRAFT_STORAGE_KEY = "kaamdesk_registration_draft";
const DEFAULT_PLAN_FEATURES = ["Tasks + attendance + leave", "WhatsApp notifications", "GST invoices"];
const PLAN_FEATURES = {
  Trial: ["Tasks, attendance, leave, KaamScore", "WhatsApp notifications", "Billing portal + GST invoices"],
  Starter: ["All task + attendance features", "Department management", "Priority WhatsApp support"],
  Growth: ["Advanced analytics + Excel exports", "Field sales GPS tracking (Phase 2)", "Dedicated account support"],
};


function RegistrationLeftPanel({ visualStep, onBackHome, copy, trialAmount }) {
  return (
    <aside className="wizard-left">
      <button className="wl-logo" type="button" onClick={onBackHome} aria-label="Go to KaamDesk home">
        <span className="mark">क</span>
        <span><span className="hi">काम</span>Desk</span>
      </button>
      <div className="wl-steps">
        {copy.steps.map(([title, subtitle], index) => {
          const number = index + 1;
          const done = number < visualStep;
          const active = number === visualStep;
          return (
            <div className="wl-step" key={title}>
              <div className="wls-line">
                <div className={`wls-dot ${done ? "done" : ""} ${active ? "active" : ""}`}>{done ? <Check size={14} /> : number}</div>
                {number < 5 && <div className={`wls-connector ${done ? "done" : ""}`} />}
              </div>
              <div className="wls-body">
                <div className={`wsb-label ${done ? "done" : ""} ${active ? "active" : ""}`}>{title}</div>
                <div className={`wsb-sub ${active ? "active" : ""}`}>{subtitle}</div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="wl-trial-pill"><b>{copy.trialTitle.replaceAll("₹9", `₹${Number(trialAmount || 9).toFixed(2)}`)}</b><span>{copy.trialCopy.replaceAll("₹9", `₹${Number(trialAmount || 9).toFixed(2)}`)}</span></div>
    </aside>
  );
}

function Registration({ language, onToggleLanguage, onBackHome, onGoLogin, onRegistrationComplete }) {
  const [step, setStep] = useState(1);
  const [setupStage, setSetupStage] = useState("setup");
  const [form, setForm] = useState(initialRegistrationForm);
  const [termsAccepted, setTermsAccepted] = useState(true);
  const [emailVerified, setEmailVerified] = useState(false);
  const [testingOtp, setTestingOtp] = useState("");
  const [otpSecondsLeft, setOtpSecondsLeft] = useState(0);
  const [otpLoading, setOtpLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState("idle");
  const [registrationLoading, setRegistrationLoading] = useState(false);
  const [registrationCompleted, setRegistrationCompleted] = useState(false);
  const [inviteSent, setInviteSent] = useState(false);
  const [managerInviteLink, setManagerInviteLink] = useState("");
  const [duplicateErrors, setDuplicateErrors] = useState({});
  const [duplicateLoading, setDuplicateLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [setupOpen, setSetupOpen] = useState({ department: false, manager: false, employee: false });
  const [progressRestored, setProgressRestored] = useState(false);
  const [draftReady, setDraftReady] = useState(false);
  const [planCatalog, setPlanCatalog] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const otpRefs = useRef([]);
  const razorpayScriptRef = useRef(null);

  const labels = copy[form.language] || copy.English;
  const registrationCopy = AUTH_PAGE_COPY.registration[language === "hi" ? "hi" : "en"];
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
  const mobileOk = /^[6-9][0-9]{9}$/.test(form.mobile);
  const passwordOk = /^(?=.{8,}$)(?=.*[A-Za-z])(?=.*\d)(?=.*[^A-Za-z\d])/.test(form.password);
  const gstOk = !form.gstin || /^[0-9A-Z]{15}$/.test(form.gstin);
  const selectedPlanRecord = planCatalog.find((plan) => plan.plan_key === form.plan) || planCatalog[0] || null;
  const selectedPlan = selectedPlanRecord ? {
    key: selectedPlanRecord.plan_key,
    title: selectedPlanRecord.plan_name,
    sub: `Up to ${Number(selectedPlanRecord.member_limit || 0).toLocaleString("en-IN")} members`,
    amount: Number(selectedPlanRecord.amount || 0),
    period: selectedPlanRecord.plan_key === "Trial" ? "30 days" : "per month",
    features: PLAN_FEATURES[selectedPlanRecord.plan_key] || DEFAULT_PLAN_FEATURES,
  } : { key: form.plan, title: form.plan, sub: "", amount: 0, period: "", features: [] };
  const gstAmount = selectedPlan.amount * 0.18;
  const totalAmount = selectedPlan.amount + gstAmount;

  const update = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    if (field === "plan" && value !== form.plan) setPaymentStatus("idle");
  };
  const clearFeedback = () => { setError(""); setMessage(""); };
  const selectLanguage = (nextLanguage) => {
    update("language", nextLanguage === "hi" ? "Hindi" : "English");
    if (language !== nextLanguage) onToggleLanguage();
  };
  const toggleRegistrationLanguage = () => selectLanguage(language === "hi" ? "en" : "hi");

  useEffect(() => {
    try {
      const rawDraft = window.sessionStorage.getItem(REGISTRATION_DRAFT_STORAGE_KEY);
      if (rawDraft) {
        const draft = JSON.parse(rawDraft);
        if (draft && typeof draft === "object") {
          if (draft.form && typeof draft.form === "object") setForm((current) => ({ ...current, ...draft.form }));
          if (Number.isInteger(Number(draft.step)) && Number(draft.step) >= 1 && Number(draft.step) <= 5) setStep(Number(draft.step));
          if (draft.emailVerified) setEmailVerified(true);
          // Browser storage is only a resume aid. It must never be treated as
          // proof that a Razorpay payment was captured.
          if (["idle", "processing", "failed"].includes(draft.paymentStatus)) setPaymentStatus(draft.paymentStatus);
        }
      }
    } catch (_) {
      window.sessionStorage.removeItem(REGISTRATION_DRAFT_STORAGE_KEY);
    } finally {
      setDraftReady(true);
    }
  }, []);

  useEffect(() => {
    if (!draftReady) return;
    try {
      const draftForm = { ...form };
      delete draftForm.password;
      window.sessionStorage.setItem('kaamdesk_registration_email', form.email || '');
      window.sessionStorage.setItem(REGISTRATION_DRAFT_STORAGE_KEY, JSON.stringify({
        step,
        form: draftForm,
        emailVerified,
        paymentStatus,
        verificationToken: form.emailVerificationToken || "",
      }));
    } catch (_) {}
  }, [draftReady, step, form, emailVerified, paymentStatus]);

  useEffect(() => {
    let active = true;
    setPlansLoading(true);
    getRegistrationPlans()
      .then((response) => {
        if (!active) return;
        const catalog = Array.isArray(response?.data?.plans) ? response.data.plans : [];
        setPlanCatalog(catalog);
        if (catalog.length && !catalog.some((plan) => plan.plan_key === form.plan)) {
          update("plan", catalog[0].plan_key);
        }
      })
      .catch(() => {
        if (active) setPlanCatalog([]);
      })
      .finally(() => {
        if (active) setPlansLoading(false);
      });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;

    async function restoreRegistrationProgress() {
      let localDraft = null;
      try {
        const rawDraft = window.sessionStorage.getItem(REGISTRATION_DRAFT_STORAGE_KEY);
        if (rawDraft) {
          const parsed = JSON.parse(rawDraft);
          if (parsed && typeof parsed === "object") localDraft = parsed;
        }
      } catch (_) {}

      try {
        const response = await getRegistrationProgress();
        const progress = response.data || {};

        if (!active) return;
        if (progress.email_verified) {
          const serverForm = progress.form || {};
          const localForm = localDraft?.form || {};
          setForm({ ...initialRegistrationForm, ...serverForm, ...localForm, emailVerificationToken: localDraft?.verificationToken || serverForm.emailVerificationToken || "" });
          const serverPaymentVerified = progress.payment_status === "success" && progress.payment_order_id;
          const restoredStep = Number(localDraft?.step || progress.step || 3);
          // Step 5 is reachable only after the server confirms a paid order.
          setStep(serverPaymentVerified ? Math.max(3, Math.min(5, restoredStep)) : Math.max(3, Math.min(4, restoredStep)));
          setSetupStage(progress.setup_stage || "setup");
          setEmailVerified(true);
          setTestingOtp("");
          setOtpSecondsLeft(0);
          setPaymentStatus(serverPaymentVerified ? "success" : (progress.payment_status === "processing" ? "processing" : "idle"));
          setRegistrationCompleted(Boolean(progress.registration_completed));
          setInviteSent(Boolean(progress.invite_sent));
          setManagerInviteLink(progress.manager_invite_link || "");
        }
      } catch (requestError) {
        // The browser draft is sufficient to restore the same wizard step after a refresh.
        if (active && localDraft) {
          setStep(Number(localDraft.step) >= 1 && Number(localDraft.step) <= 5 ? Number(localDraft.step) : 1);
          setEmailVerified(Boolean(localDraft.emailVerified));
          setPaymentStatus(["idle", "processing", "failed"].includes(localDraft.paymentStatus) ? localDraft.paymentStatus : "idle");
        }
      } finally {
        if (active) setProgressRestored(true);
      }
    }

    restoreRegistrationProgress();

    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!progressRestored || !emailVerified || registrationCompleted) return undefined;

    const timer = window.setTimeout(() => {
      const progressForm = { ...form };
      delete progressForm.password;
      saveRegistrationProgress({
        step,
        setup_stage: setupStage,
        form: progressForm,
        payment_status: paymentStatus,
        registration_completed: registrationCompleted,
        invite_sent: inviteSent,
        manager_invite_link: managerInviteLink,
      }).catch(() => {});
    }, 250);

    return () => window.clearTimeout(timer);
  }, [progressRestored, emailVerified, registrationCompleted, step, setupStage, form, paymentStatus, inviteSent, managerInviteLink]);

  useEffect(() => {
    if (otpSecondsLeft <= 0 || emailVerified) return undefined;
    const timer = window.setInterval(() => setOtpSecondsLeft((value) => Math.max(0, value - 1)), 1000);
    return () => window.clearInterval(timer);
  }, [otpSecondsLeft, emailVerified]);

  useEffect(() => {
    const payload = {};
    if (form.companyName.trim().length >= 2) payload.companyName = form.companyName.trim();
    if (/^[0-9A-Z]{15}$/.test(form.gstin)) payload.gstin = form.gstin;
    if (emailOk) payload.email = form.email.trim();
    if (mobileOk) payload.mobile = form.mobile;
    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.managerEmail)) payload.managerEmail = form.managerEmail.trim();
    if (/^[6-9][0-9]{9}$/.test(form.managerMobile)) payload.managerMobile = form.managerMobile;

    if (!Object.keys(payload).length) {
      setDuplicateErrors({});
      setDuplicateLoading(false);
      return undefined;
    }

    let active = true;
    setDuplicateLoading(true);
    const timer = window.setTimeout(async () => {
      try {
        const response = await checkRegistrationDuplicate(payload);
        if (active) setDuplicateErrors(response.errors || {});
      } catch (requestError) {
        if (active) setDuplicateErrors(requestError.errors || { api: requestError.message });
      } finally {
        if (active) setDuplicateLoading(false);
      }
    }, 450);

    return () => { active = false; window.clearTimeout(timer); };
  }, [form.companyName, form.gstin, form.email, form.mobile, form.managerEmail, form.managerMobile, emailOk, mobileOk]);

  const goStep = (nextStep) => {
    clearFeedback();
    setStep(nextStep);
    window.scrollTo(0, 0);
  };

  const sendOtpAndContinue = async () => {
    clearFeedback();
    if (form.businessType === "Other" && !form.customBusinessType.trim()) return setError("Please enter your Business Type.");
    const required = form.companyName.trim() && form.businessType && form.city.trim() && form.ownerName.trim() && mobileOk && emailOk && passwordOk && gstOk && termsAccepted;
    if (!required) return setError(passwordOk ? "Please complete all required details and accept the terms." : "Password must be at least 8 characters.");
    if (duplicateLoading) return setError("Please wait while we check your details.");
    const duplicateMessage = duplicateErrors.companyName || duplicateErrors.gstin || duplicateErrors.email || duplicateErrors.mobile;
    if (duplicateMessage) return setError(duplicateMessage);

    setOtpLoading(true);
    try {
      const response = await sendEmailOtp(form.email.trim());
      setTestingOtp(response.dev_otp || response.testing_otp || "");
      setOtpSecondsLeft(Number(response.expires_in_seconds || 300));
      update("emailOtp", "");
      update("emailVerificationToken", "");
      setEmailVerified(false);
      setMessage(response.message || "OTP sent to your email.");
      goStep(2);
    } catch (requestError) {
      const firstValidationError = Object.values(requestError.errors || {}).find((value) => typeof value === "string" && value.trim());
      setError(firstValidationError || requestError.message);
    } finally {
      setOtpLoading(false);
    }
  };

  const resendOtp = async () => {
    clearFeedback();
    setOtpLoading(true);
    try {
      const response = await sendEmailOtp(form.email.trim());
      setTestingOtp(response.dev_otp || response.testing_otp || "");
      setOtpSecondsLeft(Number(response.expires_in_seconds || 300));
      update("emailOtp", "");
      update("emailVerificationToken", "");
      setEmailVerified(false);
      setMessage(response.message || "A new OTP was sent.");
      otpRefs.current[0]?.focus();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setOtpLoading(false);
    }
  };

  const setOtpDigit = (index, rawValue) => {
    const incoming = rawValue.replace(/\D/g, "");
    if (incoming.length > 1) {
      const nextOtp = incoming.slice(0, 6);
      update("emailOtp", nextOtp);
      otpRefs.current[Math.min(nextOtp.length, 5)]?.focus();
      return;
    }
    const digits = Array.from({ length: 6 }, (_, position) => form.emailOtp[position] || "");
    digits[index] = incoming;
    update("emailOtp", digits.join(""));
    if (incoming && index < 5) otpRefs.current[index + 1]?.focus();
  };

  const verifyOtpAndContinue = async () => {
    clearFeedback();
    if (otpSecondsLeft <= 0) return setError("OTP expired. Please resend the code.");
    if (!/^\d{6}$/.test(form.emailOtp)) return setError("Enter the complete 6-digit OTP.");
    setOtpLoading(true);
    try {
      const response = await verifyEmailOtp(form.email.trim(), form.emailOtp, { ...form });
      const verifiedForm = { ...form, emailVerificationToken: response.verification_token || "" };
      setForm(verifiedForm);
      setEmailVerified(true);
      setOtpSecondsLeft(0);
      const progressForm = { ...verifiedForm };
      delete progressForm.password;
      // OTP verification itself establishes the registration session. Persisting
      // the progress is only a resume aid, so it must never block the wizard from
      // moving to the next registration step.
      saveRegistrationProgress({
        step: 3,
        setup_stage: setupStage,
        form: progressForm,
        payment_status: paymentStatus,
        registration_completed: false,
        invite_sent: false,
        manager_invite_link: "",
      }).catch(() => {});
      goStep(3);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setOtpLoading(false);
    }
  };

  const loadRazorpayCheckout = () => new Promise((resolve, reject) => {
    if (window.Razorpay) return resolve();
    if (razorpayScriptRef.current) {
      razorpayScriptRef.current.addEventListener("load", () => resolve(), { once: true });
      razorpayScriptRef.current.addEventListener("error", () => reject(new Error("Razorpay Checkout could not be loaded.")), { once: true });
      return;
    }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Razorpay Checkout could not be loaded."));
    razorpayScriptRef.current = script;
    document.body.appendChild(script);
  });

  const startRazorpayPayment = async () => {
    clearFeedback();
    setPaymentStatus("processing");
    try {
      await loadRazorpayCheckout();
      const order = await createRazorpayOrder(form.plan, form.emailVerificationToken || "", form.password || "");
      if (!order?.order_id || !order?.key_id) throw new Error("Razorpay order could not be created.");
      const razorpayTestMode = String(order.key_id).startsWith("rzp_test_");
      const checkout = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency || "INR",
        name: "KaamDesk",
        description: `${selectedPlan.title} plan`,
        order_id: order.order_id,
        prefill: { name: form.ownerName, email: form.email, contact: form.mobile ? `+91${form.mobile}` : "" },
        notes: { plan: form.plan },
        theme: { color: "#111827" },
        ...(razorpayTestMode ? {
          config: {
            display: {
              hide: [{ method: "upi" }],
            },
          },
        } : {}),
        modal: { ondismiss: () => setPaymentStatus("idle") },
        handler: async (response) => {
          try {
            const verifiedPayment = await verifyRazorpayPayment(response, form.emailVerificationToken || "");
            const paymentForm = {
              ...form,
              paymentOrderId: verifiedPayment.order_id || response.razorpay_order_id || "",
              paymentId: verifiedPayment.payment_id || response.razorpay_payment_id || "",
            };
            setForm(paymentForm);
            setPaymentStatus("success");
            const progressForm = { ...paymentForm };
            delete progressForm.password;
            await saveRegistrationProgress({ step: 4, setup_stage: "setup", form: progressForm, payment_status: "success", registration_completed: false, invite_sent: false, manager_invite_link: "" }).catch(() => {});
          } catch (requestError) {
            setPaymentStatus("failed");
            setError(requestError.message || "Payment verification failed.");
          }
        },
      });
      checkout.open();
    } catch (requestError) {
      setPaymentStatus("failed");
      setError(requestError.message || "Unable to start Razorpay payment.");
    }
  };

  const chooseDepartment = (department) => {
    const current = Array.isArray(form.departments) ? form.departments : [];
    const next = current.includes(department) ? current.filter((item) => item !== department) : [...current, department];
    update("departments", next);
    update("departmentOption", department);
    update("department", next.filter((item) => item !== "Other").join(", "));
  };

  const showWelcomeStep = () => {
    setStep(8);
    window.scrollTo(0, 0);
  };

  const updateCustomDepartment = (value) => {
    update("customDepartment", value);
  };

  const toggleSetupSection = (section) => {
    clearFeedback();
    setSetupOpen((current) => ({ ...current, [section]: !current[section] }));
  };

  const saveRegistration = async ({ openWelcome = false, skipInvites = false } = {}) => {
    clearFeedback();
    if (registrationCompleted) {
      if (openWelcome) showWelcomeStep();
      return;
    }

    const emailValid = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    const mobileValid = (value) => /^[6-9][0-9]{9}$/.test(value);
    const businessType = form.businessType === "Other" ? form.customBusinessType.trim() : form.businessType;
    if (form.businessType === "Other" && !businessType) return setError("Please enter your Business Type.");
    const selectedDepartments = Array.isArray(form.departments) ? form.departments.filter((item) => item !== "Other") : [];
    if (!skipInvites && setupOpen.department && form.departments?.includes("Other") && !form.customDepartment.trim()) return setError("Enter your custom department name.");
    const departments = [...selectedDepartments, ...(form.departments?.includes("Other") && form.customDepartment.trim() ? [form.customDepartment.trim()] : [])];
    if (!skipInvites && setupOpen.department && departments.length === 0) return setError("Select at least one department.");

    const managerReady = form.managerName.trim() && emailValid(form.managerEmail) && mobileValid(form.managerMobile) && form.managerDepartment;
    const employeeReady = form.employeeName.trim() && emailValid(form.employeeEmail) && mobileValid(form.employeeMobile) && form.employeeDepartment;
    if (!skipInvites && setupOpen.manager && !managerReady) return setError("Complete all Manager fields before continuing.");
    if (!skipInvites && setupOpen.employee && !employeeReady) return setError("Complete all Employee fields before continuing.");
    if (!skipInvites && setupOpen.manager && (duplicateErrors.managerEmail || duplicateErrors.managerMobile)) return setError(duplicateErrors.managerEmail || duplicateErrors.managerMobile);

    setRegistrationLoading(true);
    try {
      await submitRegistration({
        ...form,
        businessType,
        departments,
        department: departments[0] || "",
        managerName: setupOpen.manager ? form.managerName.trim() : "",
        managerEmail: setupOpen.manager ? form.managerEmail.trim() : "",
        managerMobile: setupOpen.manager ? form.managerMobile : "",
        managerDepartment: setupOpen.manager ? form.managerDepartment : "",
        employeeName: setupOpen.employee ? form.employeeName.trim() : "",
        employeeEmail: setupOpen.employee ? form.employeeEmail.trim() : "",
        employeeMobile: setupOpen.employee ? form.employeeMobile : "",
        employeeDepartment: setupOpen.employee ? form.employeeDepartment : "",
        sendManagerInvite: !skipInvites && setupOpen.manager,
        sendEmployeeInvite: !skipInvites && setupOpen.employee,
        skipInvites,
      });
      setRegistrationCompleted(true);
      try { window.sessionStorage.removeItem(REGISTRATION_DRAFT_STORAGE_KEY); window.sessionStorage.removeItem('kaamdesk_registration_email'); } catch (_) {}
      await clearRegistrationProgress().catch(() => {});
      setInviteSent(!skipInvites && Boolean(setupOpen.manager || setupOpen.employee));
      setMessage(!skipInvites && (setupOpen.manager || setupOpen.employee) ? "Invitation Sent Successfully" : "");
      if (openWelcome) {
        window.setTimeout(showWelcomeStep, !skipInvites && (setupOpen.manager || setupOpen.employee) ? 900 : 0);
      }
    } catch (requestError) {
      setDuplicateErrors((current) => ({ ...current, ...(requestError.errors || {}) }));
      if (requestError.errors?.payment) {
        // A stale browser state can never continue setup without a paid order.
        setPaymentStatus("idle");
        setStep(4);
      }
      const firstValidationError = Object.values(requestError.errors || {}).find((value) => typeof value === "string" && value.trim());
      setError(firstValidationError || requestError.message);
    } finally {
      setRegistrationLoading(false);
    }
  };
  const handleRegistrationComplete = async () => {
    if (!registrationCompleted) {
      setError("Please complete registration before opening the dashboard.");
      return;
    }
    try {
      await onRegistrationComplete();
    } catch (transitionError) {
      setError(transitionError?.message || "Your login session could not be restored. Please try again.");
    }
  };
  const handleRegistrationNavigation = registrationCompleted ? handleRegistrationComplete : onBackHome;
  const headerActionLabel = registrationCompleted ? (language === "hi" ? "डैशबोर्ड" : "Dashboard") : (language === "hi" ? "लॉगिन" : "Login");

  const visualStep = Math.min(step, 5);
  const otpDigits = Array.from({ length: 6 }, (_, index) => form.emailOtp[index] || "");
  const timerText = `${String(Math.floor(otpSecondsLeft / 60)).padStart(2, "0")}:${String(otpSecondsLeft % 60).padStart(2, "0")}`;

  return (
    <div className="kd-registration-page">
      <div className="kd-login-topbar"><HomeHeader onStart={registrationCompleted ? handleRegistrationComplete : onGoLogin} onBackHome={handleRegistrationNavigation} minimal language={language} onToggleLanguage={toggleRegistrationLanguage} minimalActionLabel={headerActionLabel} /></div>
      <main className="kd-owner-onboarding kd-home-wrap">
        <div className="wizard-shell">
          <RegistrationLeftPanel visualStep={step === 8 ? 6 : visualStep} onBackHome={handleRegistrationNavigation} copy={registrationCopy} trialAmount={planCatalog.find((plan) => plan.plan_key === "Trial")?.amount} />
        <section className="wizard-right">
          <div className="wr-top"><div className="wr-progress"><div className="wr-progress-fill" style={{ width: `${visualStep * 20}%` }} /></div><div className="wr-step-label">{step === 8 ? "Registration complete" : setupStage === "invite" ? "Finishing up" : `Step ${visualStep} of 5`}</div></div>
          {error && <div className="wizard-alert error" role="alert">{error}</div>}
          {message && <div className="wizard-alert success">{message}</div>}

          {step === 8 && <DashboardLandingStep form={form} labels={labels} onRestart={handleRegistrationComplete} planCatalog={planCatalog} />}

          {step === 1 && (
            <div className="step-content active">
              <div className="step-eyebrow">{registrationCopy.stepOne}</div><h1 className="step-title">{registrationCopy.companyTitle}</h1><p className="step-sub">{registrationCopy.companyCopy}</p>
              <div className="form-field compact"><label>{registrationCopy.languageLabel}</label><div className="lang-toggle">{["English", "Hindi"].map((option) => <button type="button" key={option} data-no-translate="true" className={`lang-btn ${form.language === option ? "selected" : ""}`} onClick={() => selectLanguage(option === "Hindi" ? "hi" : "en")}><span className="lb-hi">{option === "English" ? "A" : "अ"}</span>{option === "English" ? "English" : "हिंदी"}</button>)}</div></div>
              <div className="form-field"><label>{registrationCopy.companyName} <span className="optional">{registrationCopy.companyRegistered}</span></label><input value={form.companyName} onChange={(event) => update("companyName", event.target.value)} placeholder="e.g. Saraf Traders Pvt Ltd" />{duplicateErrors.companyName && <small className="field-error">{duplicateErrors.companyName}</small>}</div>
              <div className="form-row"><div className="form-field"><label>{registrationCopy.businessType}</label><select value={form.businessType} onChange={(event) => { const businessType = event.target.value; update("businessType", businessType); if (businessType !== "Other") update("customBusinessType", ""); }}><option value="">{registrationCopy.selectType}</option>{BUSINESS_TYPES.map((type) => <option key={type}>{type}</option>)}</select></div><div className="form-field"><label>{registrationCopy.city}</label><input value={form.city} onChange={(event) => update("city", event.target.value)} placeholder="e.g. Delhi" /></div></div>
              {form.businessType === "Other" && <div className="form-field"><label>Business Type Name</label><input value={form.customBusinessType} onChange={(event) => update("customBusinessType", event.target.value)} placeholder="Enter your business type" /></div>}
              <div className="form-field"><label>{registrationCopy.gstNumber} <span className="optional">{registrationCopy.gstHelp}</span></label><input className="mono" value={form.gstin} onChange={(event) => update("gstin", event.target.value.toUpperCase().slice(0, 15))} placeholder="e.g. 27AADCB2230M1Z3" /><div className="helper">{registrationCopy.gstCopy}</div>{duplicateErrors.gstin && <small className="field-error">{duplicateErrors.gstin}</small>}</div>
              <div className="form-row"><div className="form-field"><label>{registrationCopy.fullName}</label><div className="input-icon"><User size={17} /><input value={form.ownerName} onChange={(event) => update("ownerName", event.target.value)} placeholder="e.g. Anil Nathan" /></div></div><div className="form-field"><label>{registrationCopy.email}</label><div className="input-icon"><Mail size={17} /><input type="email" value={form.email} onChange={(event) => { update("email", event.target.value); setEmailVerified(false); setTestingOtp(""); }} placeholder="owner@company.com" /></div>{duplicateErrors.email && <small className="field-error">{duplicateErrors.email}</small>}</div></div>
              <div className="form-field"><label>{registrationCopy.password}</label><div className="input-icon"><LockKeyhole size={17} /><input type="password" value={form.password} onChange={(event) => update("password", event.target.value)} placeholder="At least 8 characters" autoComplete="new-password" /></div><div className="helper">{registrationCopy.passwordHelp}</div><div className="password-rules" aria-live="polite"><small className={form.password.length >= 8 ? "valid" : ""}>At least 8 characters</small><small className={/[A-Za-z]/.test(form.password) && /\d/.test(form.password) ? "valid" : ""}>One letter and one number</small><small className={/[^A-Za-z\d]/.test(form.password) ? "valid" : ""}>At least one symbol, e.g. @#$</small></div></div>
              <div className="form-field"><label>{registrationCopy.mobile}</label><div className="phone-row"><div className="phone-cc">+91</div><input className="mono" value={form.mobile} onChange={(event) => update("mobile", event.target.value.replace(/\D/g, "").slice(0, 10))} placeholder="98765 43210" /></div><div className="helper">{registrationCopy.mobileCopy}</div>{duplicateErrors.mobile && <small className="field-error">{duplicateErrors.mobile}</small>}</div>
              <label className="terms-row"><input type="checkbox" checked={termsAccepted} onChange={(event) => setTermsAccepted(event.target.checked)} /><span>{registrationCopy.terms}</span></label>
              <button className="btn btn-primary" type="button" onClick={sendOtpAndContinue} disabled={otpLoading || duplicateLoading}>{otpLoading ? registrationCopy.sendingEmailOtp : <>{registrationCopy.continueVerify} <ArrowRight size={18} /></>}</button>
            </div>
          )}

          {step === 2 && (
            <div className="step-content active">
              <button className="back-link" type="button" onClick={() => goStep(1)}><ArrowLeft size={15} /> Change details</button><div className="step-eyebrow">Step 2 of 5</div><h1 className="step-title">Verify your email</h1><p className="step-sub">We sent a 6-digit code to <b>{form.email}</b>. Enter it below to secure your owner account.</p>
              <div className="email-note"><Mail size={18} /><span>Email OTP sent to <b>{form.email}</b>.</span></div>
              {testingOtp && <div className="testing-otp"><LockKeyhole size={18} /><span>Testing Email OTP: <b>{testingOtp}</b></span></div>}
              <div className="form-field"><label>Enter the 6-digit code</label><div className="otp-boxes">{otpDigits.map((digit, index) => <input key={index} ref={(element) => { otpRefs.current[index] = element; }} inputMode="numeric" autoComplete={index === 0 ? "one-time-code" : "off"} value={digit} maxLength={1} className={digit ? "filled" : ""} aria-label={`OTP digit ${index + 1}`} onChange={(event) => setOtpDigit(index, event.target.value)} onKeyDown={(event) => { if (event.key === "Backspace" && !digit && index > 0) otpRefs.current[index - 1]?.focus(); }} />)}</div><div className="otp-meta"><span>Expires in <b>{timerText}</b></span><button type="button" onClick={resendOtp} disabled={otpLoading}>Resend code</button></div></div>
              <button className="btn btn-primary" type="button" onClick={verifyOtpAndContinue} disabled={otpLoading}>{otpLoading ? "Verifying..." : <>Verify and continue <ArrowRight size={18} /></>}</button><button className="btn btn-ghost" type="button" onClick={() => goStep(1)}>Use a different email</button>
            </div>
          )}

          {step === 3 && (
            <div className="step-content active">
              <button className="back-link" type="button" onClick={() => goStep(2)}><ArrowLeft size={15} /> Back</button><div className="step-eyebrow">Step 3 of 5</div><h1 className="step-title">Choose your plan</h1><p className="step-sub">Choose an active plan from the current platform pricing. Amounts are loaded from the same plan catalogue managed by Super Admin.</p>
              {plansLoading ? <div className="plan-grid"><div className="wizard-alert">Loading plans...</div></div> : null}
              {!plansLoading && planCatalog.length ? <div className="plan-grid">{planCatalog.map((plan) => { const details = { title: plan.plan_name, sub: `Up to ${Number(plan.member_limit || 0).toLocaleString("en-IN")} members`, amount: Number(plan.amount || 0), period: plan.plan_key === "Trial" ? "30 days" : "per month", features: PLAN_FEATURES[plan.plan_key] || DEFAULT_PLAN_FEATURES }; return <button type="button" key={plan.plan_key} className={`onboarding-plan-card ${form.plan === plan.plan_key ? "selected" : ""} ${plan.plan_key === "Trial" ? "recommended" : ""}`} onClick={() => update("plan", plan.plan_key)}><span className="pc-radio" /><span className="pc-body"><strong>{details.title}</strong><small>{details.sub}</small><ul>{details.features.map((feature) => <li key={feature}>{feature}</li>)}</ul></span><span className="pc-price"><b>₹{details.amount.toFixed(2)}</b><small>{details.period}</small><em>+ GST</em></span></button>; })}</div> : null}
              <div className="plan-summary"><div><span>Plan</span><b>{selectedPlan.title} — {selectedPlan.period}</b></div><div><span>Amount</span><b>₹{selectedPlan.amount.toFixed(2)}</b></div><div><span>GST (18%)</span><b>₹{gstAmount.toFixed(2)}</b></div><div><span>Total payable</span><b>₹{totalAmount.toFixed(2)}</b></div></div>
              <button className="btn btn-primary" type="button" onClick={() => goStep(4)}>Continue to payment <ArrowRight size={18} /></button><div className="secure-note"><LockKeyhole size={14} /> Secure Razorpay checkout · UPI, QR, Card, Net Banking</div>
            </div>
          )}

          {step === 4 && (
            <div className="step-content active payment-step">
              <div className="step-eyebrow">Step 4 of 5</div><h1 className="step-title">Payment</h1>
              {paymentStatus === "idle" && <><p className="step-sub">Pay securely with Razorpay for your {selectedPlan.title} plan. UPI, QR, cards and net banking are available in Live Mode.</p><div className="payment-total"><span>Total payable</span><b>₹{totalAmount.toFixed(2)}</b><small>Including 18% GST</small></div><div className="secure-note"><LockKeyhole size={14} /> Test Mode uses Razorpay's simulated card/netbanking flow. UPI Intent and QR are available in Live Mode.</div><button className="btn btn-primary" type="button" onClick={startRazorpayPayment}>Pay ₹{totalAmount.toFixed(2)} <ArrowRight size={18} /></button></>}
              {paymentStatus === "processing" && <div className="payment-processing"><div className="payment-spinner" /><div><h3>Opening secure payment</h3><p>Razorpay is processing your payment. Please complete the payment and keep this window open.</p></div></div>}
              {paymentStatus === "failed" && <div className="payment-processing"><div><h3>Payment could not be completed</h3><p>{error || "Your payment was not verified. You can safely retry."}</p></div><button className="btn btn-primary" type="button" onClick={startRazorpayPayment}>Retry Payment <ArrowRight size={18} /></button></div>}
              {paymentStatus === "success" && <div className="payment-processing"><div className="payment-success-icon"><Check size={30} /></div><div><h3>Payment Successful</h3><p>₹{totalAmount.toFixed(2)} paid securely with Razorpay. Your GST invoice will be generated after registration.</p></div><div className="payment-reference"><span>Payment</span><b>Verified</b><span>Plan</span><b>{selectedPlan.title}</b></div><button className="btn btn-primary" type="button" onClick={() => goStep(5)}>Continue <ArrowRight size={18} /></button></div>}
            </div>
          )}

          {step === 5 && (
            <div className="step-content active kd-setup-company-step">
              <div className="kd-setup-accordion">
                <div className={`kd-setup-item ${setupOpen.department ? "open" : ""}`}>
                  <button type="button" className="kd-setup-trigger" onClick={() => toggleSetupSection("department")}><span className="kd-setup-icon"><Building2 size={22} /></span><strong>Add Department</strong><ChevronDown size={20} /></button>
                  {setupOpen.department && <div className="kd-setup-panel"><label>Select Department</label><div className="dept-chip-row">{DEPARTMENTS.map((department) => <button type="button" key={department} className={`dept-chip ${form.departments?.includes(department) ? "selected" : ""}`} onClick={() => chooseDepartment(department)}>{department}</button>)}</div>{form.departments?.includes("Other") && <div className="form-field"><label>Other Department Name</label><input value={form.customDepartment} onChange={(event) => updateCustomDepartment(event.target.value)} placeholder="Enter department name" /></div>}</div>}
                </div>

                <div className={`kd-setup-item ${setupOpen.manager ? "open" : ""}`}>
                  <button type="button" className="kd-setup-trigger" onClick={() => toggleSetupSection("manager")}><span className="kd-setup-icon"><User size={22} /></span><strong>Add Manager</strong><ChevronDown size={20} /></button>
                  {setupOpen.manager && <div className="kd-setup-panel kd-setup-grid"><div className="form-field"><label>Name</label><input value={form.managerName} onChange={(e) => update("managerName", e.target.value)} placeholder="Enter manager name" /></div><div className="form-field"><label>Email</label><input type="email" value={form.managerEmail} onChange={(e) => update("managerEmail", e.target.value)} placeholder="Enter email address" /></div><div className="form-field"><label>Mobile Number</label><input value={form.managerMobile} onChange={(e) => update("managerMobile", e.target.value.replace(/\D/g, "").slice(0, 10))} placeholder="Enter mobile number" /></div><div className="form-field"><label>Department</label><select value={form.managerDepartment} onChange={(e) => update("managerDepartment", e.target.value)}><option value="">Select department</option>{[...(form.departments || []).filter((d) => d !== "Other"), ...(form.customDepartment.trim() ? [form.customDepartment.trim()] : [])].map((d) => <option key={d}>{d}</option>)}</select></div></div>}
                </div>

                <div className={`kd-setup-item ${setupOpen.employee ? "open" : ""}`}>
                  <button type="button" className="kd-setup-trigger" onClick={() => toggleSetupSection("employee")}><span className="kd-setup-icon"><Users size={22} /></span><strong>Add Employee</strong><ChevronDown size={20} /></button>
                  {setupOpen.employee && <div className="kd-setup-panel kd-setup-grid"><div className="form-field"><label>Name</label><input value={form.employeeName} onChange={(e) => update("employeeName", e.target.value)} placeholder="Enter employee name" /></div><div className="form-field"><label>Email</label><input type="email" value={form.employeeEmail} onChange={(e) => update("employeeEmail", e.target.value)} placeholder="Enter email address" /></div><div className="form-field"><label>Mobile Number</label><input value={form.employeeMobile} onChange={(e) => update("employeeMobile", e.target.value.replace(/\D/g, "").slice(0, 10))} placeholder="Enter mobile number" /></div><div className="form-field"><label>Department</label><select value={form.employeeDepartment} onChange={(e) => update("employeeDepartment", e.target.value)}><option value="">Select department</option>{[...(form.departments || []).filter((d) => d !== "Other"), ...(form.customDepartment.trim() ? [form.customDepartment.trim()] : [])].map((d) => <option key={d}>{d}</option>)}</select></div></div>}
                </div>
              </div>
              <div className="kd-setup-actions">
                <button className="btn btn-ghost kd-setup-skip" type="button" onClick={() => saveRegistration({ openWelcome: true, skipInvites: true })} disabled={registrationLoading}>
                  Skip
                </button>
                <button className="btn btn-primary kd-setup-submit" type="button" onClick={() => saveRegistration({ openWelcome: true })} disabled={registrationLoading}>
                  {registrationLoading ? "Sending Invitation..." : <>Invite & Continue <ArrowRight size={18} /></>}
                </button>
              </div>
            </div>
          )}
          </section>
        </div>
      </main>
      {/* <div className="kd-login-footer"><HomeFooter onBackHome={handleRegistrationNavigation} language={language} /></div> */}
    </div>
  );
}

export default Registration;
